import { useContext, useMemo } from 'react';
import { useParams } from 'react-router-dom';

import { isTFolder, TFolder, TPath, useFolder } from '@modules/items';
import { SignInContext } from '@modules/users';
import { NoPage } from '@shared/pages';
import { StatusLabel } from '@shared/components';
import { useDocumentTitle } from '@shared/hooks';

import CreateChangeRequestScreen from '../components/CreateChangeRequestScreen';
import { getChangeCreatePath, getChangeDashboardPath, isChangeMockVisible } from '../helpers';

const buildFullPath = (folder: TFolder, productId?: string): TPath => [
  ...folder.resolvedPath,
  { _id: folder._id, name: folder.name, accessRight: folder.accessRight },
  {
    _id: 'changes',
    name: 'Changes',
    accessRight: folder.accessRight,
    href: getChangeDashboardPath(productId),
  },
  {
    _id: 'create-change',
    name: 'Create change request',
    accessRight: folder.accessRight,
    href: getChangeCreatePath(productId),
  },
];

const MockChangeCreate = () => {
  const { productId } = useParams();
  const { organization } = useContext(SignInContext);
  const config = organization.mock?.change;
  const folderId = productId ?? organization.folders.qm;
  const isVisible = isChangeMockVisible(config) && Boolean(folderId);

  const { error, data } = useFolder(folderId, { enabled: isVisible });

  useDocumentTitle('Create change request');

  const scope = productId ? 'product' : 'company';
  const fullPath = useMemo(
    () => (data && isTFolder(data) ? buildFullPath(data as TFolder, productId) : undefined),
    [data, productId]
  );

  if (!isVisible || !config) return <NoPage />;

  return (
    <StatusLabel
      data={data}
      loadingMessage="Loading change request..."
      error={error}
      errorMessage="Loading change request failed!"
      render={(resolvedData) =>
        isTFolder(resolvedData) && fullPath ? (
          <CreateChangeRequestScreen
            folder={resolvedData as TFolder}
            fullPath={fullPath}
            config={config}
            scope={scope}
          />
        ) : (
          <NoPage />
        )
      }
    />
  );
};

export default MockChangeCreate;
